import {
    LazyModule,
    LazyAction,
    createLazyComponent,
    registerLazyOrchestrator,
    type LazyComponent,
} from 'owa-bundling';
import { orchestrator } from 'satcheljs';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { SearchScenarioId } from 'owa-search-store';
import type * as lazyIndex from './lazyIndex';
import type { CopilotSearchAnswerProps } from './components/CopilotSearchAnswer';
import { loadTableViewFromSearchTableQuery } from 'owa-mail-search';
import { isSearchPlusChatFeatureEnabledForSearchSession } from 'owa-mail-search/lib/utils/isSearchPlusChatFeatureEnabledForSearchSession';
import { endSearchSession, searchSessionStarted } from 'owa-search-actions';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "CopilotSearchAnswer" */ './lazyIndex'),
    { name: 'CopilotSearchAnswer' }
);

// Lazy component for rendering in search results
export const LazyCopilotSearchAnswer: LazyComponent<{}, unknown, void> = createLazyComponent(
    lazyModule,
    (m: typeof lazyIndex) => m.CopilotSearchAnswer
);

// Lazy container that displays the copilot answer
export const LazyCopilotSearchAnswerContainer: LazyComponent<CopilotSearchAnswerProps, void> =
    createLazyComponent(lazyModule, (m: typeof lazyIndex) => m.CopilotSearchAnswerContainer);

// @1js/scc-react-markdown-renderer (loaded via lazyIndex) constructs regexes with negative
// lookbehind at module-eval time, which throws on Safari < 16.4 and fails the chunk load.
// Feature-test here rather than UA-sniff because isBrowserSafari/isMinimumBrowserVersion are
// blocked at module scope by owa-custom-rules/no-side-effects.
// TODO: Remove this gate once @1js/scc-react-markdown-renderer drops the lookbehind regexes.
// Tracked in https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/429095
let supportsRegexLookbehind = false;
try {
    new RegExp('(?<!a)b');
    supportsRegexLookbehind = true;
} catch {
    // Lookbehind unsupported (Safari < 16.4); leave supportsRegexLookbehind = false.
}

// Prewarm-on-focus entry point. Unlike the start/clear orchestrators, the flight/scenario gate is
// checked in this non-lazy module BEFORE the chunk is imported: searchSessionStarted fires on every
// search-box focus, so registering a lazy orchestrator here would download the CopilotSearchAnswer
// chunk for non-treatment users just from focusing the box. The LazyAction defers the import until
// the gate passes.
const lazyPrewarmCopilotSearchConnection: LazyAction<
    typeof lazyIndex.prewarmCopilotSearchConnection,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.prewarmCopilotSearchConnection);

if (supportsRegexLookbehind) {
    // Run the Copilot answer when a mail search executes.
    registerLazyOrchestrator(
        loadTableViewFromSearchTableQuery,
        lazyModule,
        m => m.startCopilotSearchOrchestrator
    );

    // Pre-open the Sydney socket when a mail search session starts (search-box focus) so the
    // connection handshake is off the critical path by the time a search answer is requested.
    orchestrator(searchSessionStarted, actionMessage => {
        if (
            !isGlobalFeatureEnabled('sea-copilotSearchAnswer') ||
            !isGlobalFeatureEnabled('sea-copilotSearchPrewarm') ||
            actionMessage.scenarioId !== SearchScenarioId.Mail
        ) {
            return;
        }
        if (!isSearchPlusChatFeatureEnabledForSearchSession(actionMessage.scenarioId)) {
            return;
        }
        return lazyPrewarmCopilotSearchConnection.importAndExecute();
    });

    // Tear down any Copilot search connection/state when the search session ends.
    registerLazyOrchestrator(endSearchSession, lazyModule, m => m.clearCopilotSearchOrchestrator);
}

// Re-export selectors for checking state
export { getCopilotSearchAnswer, getIsLoading, getIsScrollMinimized } from './selectors/selectors';

// Re-export mutators that need to be called from outside the lazy chunk
export { setScrollMinimizedMutator } from './mutators/mutators';
